<script lang="ts">
  import { Seguros, Cuentas, type Aseguradora, type CoberturaResult, type Cuenta } from '../lib/api';

  let lista = $state<Aseguradora[]>([]);
  let nueva = $state<Aseguradora>({ codigo:'', nombre:'', nit:'', contacto:'', telefono:'', email:'', porcentajeDefault: 80, activa: true });
  let soloActivas = $state(true);
  let cargando = $state(false);
  let error = $state<string|null>(null);
  let ok = $state<string|null>(null);

  let cuentas = $state<Cuenta[]>([]);
  let cuentaId = $state('');
  let pctOverride = $state<number|null>(null);
  let resultado = $state<CoberturaResult|null>(null);

  async function recargar() {
    cargando = true; error = null;
    try { lista = await Seguros.list(soloActivas); }
    catch (e: any) { error = e.message; }
    cargando = false;
  }
  async function recargarCuentas() {
    try { cuentas = await Cuentas.list(); } catch (e: any) { error = e.message; }
  }
  async function crear() {
    error = null; ok = null;
    if (!nueva.codigo || !nueva.nombre) { error = 'Código y nombre requeridos'; return; }
    try {
      await Seguros.crear(nueva);
      ok = `Aseguradora "${nueva.nombre}" creada.`;
      nueva = { codigo:'', nombre:'', nit:'', contacto:'', telefono:'', email:'', porcentajeDefault: 80, activa: true };
      await recargar();
    } catch (e: any) { error = e.message; }
  }
  async function toggleActiva(a: Aseguradora) {
    try { await Seguros.actualizar(a.id!, { ...a, activa: !a.activa }); await recargar(); }
    catch (e: any) { error = e.message; }
  }
  async function calcular() {
    error = null; resultado = null;
    if (!cuentaId) { error = 'Selecciona una cuenta'; return; }
    try {
      const body = pctOverride != null ? { porcentajeCobertura: pctOverride } : {};
      resultado = await Seguros.calcular(cuentaId, body);
    } catch (e: any) { error = e.message; }
  }

  function fmtQ(n: number) {
    return new Intl.NumberFormat('es-GT', { style:'currency', currency:'GTQ', minimumFractionDigits:2 }).format(n);
  }

  $effect(() => { recargar(); recargarCuentas(); });
</script>

<div class="page">
  <!-- Header -->
  <div class="page-header">
    <div class="header-icon">🛡️</div>
    <div class="header-info">
      <h1>Aseguradoras / Seguros</h1>
      <p>Gestión de pólizas, coberturas y cálculo de deducibles</p>
    </div>
  </div>

  {#if error}<div class="msg msg-error">{error}</div>{/if}
  {#if ok}<div class="msg msg-ok">{ok}</div>{/if}

  <div class="row-2">
    <!-- Nueva aseguradora -->
    <div class="card">
      <div class="card-hd">➕ Nueva aseguradora</div>
      <div class="card-body">
        <div class="form-grid">
          <input placeholder="Código *" bind:value={nueva.codigo}/>
          <input placeholder="Nombre *" bind:value={nueva.nombre}/>
          <input placeholder="NIT" bind:value={nueva.nit}/>
          <input placeholder="Contacto" bind:value={nueva.contacto}/>
          <input placeholder="Teléfono" bind:value={nueva.telefono}/>
          <input placeholder="Email" bind:value={nueva.email}/>
          <label class="field">
            <span>% Cobertura default</span>
            <input type="number" min="0" max="100" step="0.01" bind:value={nueva.porcentajeDefault}/>
          </label>
        </div>
        <button class="btn-primary" onclick={crear}>Crear aseguradora</button>
      </div>
    </div>

    <!-- Calcular cobertura -->
    <div class="card">
      <div class="card-hd">🧮 Calcular cobertura</div>
      <div class="card-body">
        <div class="calc-form">
          <label class="field">
            <span>Cuenta</span>
            <select bind:value={cuentaId}>
              <option value="">— Elegir cuenta —</option>
              {#each cuentas as c (c.id)}
                <option value={c.id}>Cuenta {c.id?.slice(0,8)} · Q{c.total ?? 0} · {c.estado}</option>
              {/each}
            </select>
          </label>
          <label class="field">
            <span>% Cobertura (opcional)</span>
            <input type="number" min="0" max="100" step="0.01" bind:value={pctOverride} placeholder="Usa el default de la aseguradora"/>
          </label>
          <button class="btn-primary" onclick={calcular}>Calcular</button>
        </div>
        {#if resultado}
          <div class="result-card">
            <div class="result-hd">Resultado de cobertura</div>
            <div class="result-grid">
              <div class="r-item"><span>Total</span><strong>{fmtQ(resultado.total)}</strong></div>
              <div class="r-item"><span>Deducible</span><strong>{fmtQ(resultado.deducible)}</strong></div>
              <div class="r-item"><span>Copago</span><strong>{fmtQ(resultado.copago)}</strong></div>
              <div class="r-item"><span>% Cobertura</span><strong>{resultado.porcentajeCobertura}%</strong></div>
              <div class="r-item accent"><span>Paga aseguradora</span><strong>{fmtQ(resultado.pagaAseguradora)}</strong></div>
              <div class="r-item warn"><span>Paga paciente</span><strong>{fmtQ(resultado.pagaPaciente)}</strong></div>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>

  <!-- Catálogo -->
  <div class="card">
    <div class="card-hd">
      <span>📋 Catálogo ({lista.length})</span>
      <div class="hd-actions">
        <label class="chk"><input type="checkbox" bind:checked={soloActivas} onchange={recargar}/> Solo activas</label>
        <button class="btn-sm" onclick={recargar} disabled={cargando}>🔄 Recargar</button>
      </div>
    </div>
    <div class="card-body nopad">
      <table>
        <thead><tr><th>Código</th><th>Nombre</th><th>NIT</th><th>Contacto</th><th>% def.</th><th>Estado</th><th></th></tr></thead>
        <tbody>
          {#each lista as a (a.id)}
            <tr class:inactiva={!a.activa}>
              <td><code>{a.codigo}</code></td>
              <td><strong>{a.nombre}</strong></td>
              <td>{a.nit ?? '—'}</td>
              <td class="muted">{a.contacto ?? '—'}{#if a.telefono} · {a.telefono}{/if}</td>
              <td><span class="pill">{a.porcentajeDefault}%</span></td>
              <td>
                <span class="status" class:on={a.activa}>{a.activa ? 'Activa' : 'Inactiva'}</span>
              </td>
              <td><button class="btn-sm" onclick={() => toggleActiva(a)}>{a.activa ? 'Desactivar' : 'Activar'}</button></td>
            </tr>
          {/each}
          {#if lista.length === 0 && !cargando}
            <tr><td colspan="7" class="empty">No hay aseguradoras registradas.</td></tr>
          {/if}
        </tbody>
      </table>
    </div>
  </div>
</div>

<style>
  .page { display: flex; flex-direction: column; gap: 0.85rem; }

  .page-header {
    display: flex; align-items: center; gap: 1rem; padding: 1.4rem 1.6rem;
    background: linear-gradient(135deg, #0d9488, #0f766e, #0c4a6e); background-size: 200% 200%;
    animation: hShift 8s ease infinite; border-radius: var(--radius-xl); color: #fff;
  }
  .header-icon { font-size: 2rem; width: 52px; height: 52px; display: flex; align-items: center; justify-content: center;
    background: rgba(255,255,255,0.15); border-radius: 14px; flex-shrink: 0; }
  .header-info h1 { margin: 0; font-size: 1.3rem; font-weight: 800; color: #fff !important; }
  .header-info p { margin: 0.2rem 0 0; font-size: 0.8rem; color: rgba(255,255,255,0.7); }
  @keyframes hShift { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }

  .msg { padding: 0.6rem 0.9rem; border-radius: 0.7rem; font-size: 0.82rem; font-weight: 600; }
  .msg-error { background: rgba(239,68,68,0.08); color: #dc2626; border: 1px solid rgba(239,68,68,0.2); }
  .msg-ok { background: rgba(16,185,129,0.08); color: #059669; border: 1px solid rgba(16,185,129,0.2); }

  .row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 0.85rem; }
  @media (max-width: 800px) { .row-2 { grid-template-columns: 1fr; } }

  .card { background: var(--glass-bg-card); backdrop-filter: var(--glass-blur); -webkit-backdrop-filter: var(--glass-blur);
    border: 1px solid var(--glass-border); border-radius: var(--radius-xl); overflow: hidden; box-shadow: var(--glass-shadow); }
  .card-hd { display: flex; justify-content: space-between; align-items: center; padding: 0.7rem 1.1rem;
    background: color-mix(in srgb, var(--brand,#0d9488) 4%, transparent); border-bottom: 1px solid var(--glass-border);
    font-weight: 700; font-size: 0.85rem; color: var(--text-primary); }
  .hd-actions { display: flex; align-items: center; gap: 0.5rem; }
  .card-body { padding: 1.1rem; }
  .card-body.nopad { padding: 0; }

  .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.55rem; margin-bottom: 0.75rem; }
  .calc-form { display: flex; flex-direction: column; gap: 0.65rem; margin-bottom: 0.75rem; }
  .field { display: flex; flex-direction: column; gap: 0.2rem; }
  .field span { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--text-secondary); }
  .field input, .field select { padding: 0.5rem 0.6rem; border: 1px solid var(--glass-border); border-radius: 0.55rem; background: rgba(255,255,255,0.6); font: inherit; font-size: 0.82rem; color: var(--text-primary); }
  .field input:focus, .field select:focus { outline: none; border-color: var(--brand,#0d9488); }

  .chk { display: flex; align-items: center; gap: 0.3rem; font-size: 0.78rem; color: var(--text-secondary); cursor: pointer; }

  .btn-primary { background: var(--brand,#0d9488); border: 0; color: #fff; font-weight: 700; padding: 0.55rem 1.1rem;
    border-radius: 0.6rem; cursor: pointer; font: inherit; font-size: 0.85rem;
    box-shadow: 0 4px 14px color-mix(in srgb, var(--brand,#0d9488) 30%, transparent); transition: all .2s; }
  .btn-primary:hover { background: var(--brand-deep,#0f766e); box-shadow: 0 6px 20px color-mix(in srgb, var(--brand,#0d9488) 40%, transparent); transform: translateY(-1px); }

  .btn-sm { padding: 0.3rem 0.6rem; border-radius: 0.5rem; border: 1px solid var(--glass-border);
    background: var(--glass-bg-card); backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px);
    color: var(--text-secondary); cursor: pointer; font: inherit; font-size: 0.75rem; font-weight: 600; transition: all .2s; }
  .btn-sm:hover { background: var(--brand-surface); border-color: var(--brand,#0d9488); color: var(--brand-deep); }

  table { width: 100%; border-collapse: collapse; }
  th { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-secondary); padding: 0.5rem 0.7rem;
    background: color-mix(in srgb, var(--brand,#0d9488) 4%, transparent); font-weight: 700;
    border-bottom: 1px solid var(--glass-border); text-align: left; }
  td { padding: 0.5rem 0.7rem; border-top: 1px solid rgba(0,0,0,0.03); font-size: 0.83rem; }
  tr:hover td { background: rgba(255,255,255,0.4); }
  tr.inactiva td { opacity: 0.55; }
  code { font-family: monospace; font-size: 0.8rem; }
  .muted { color: var(--text-muted); font-size: 0.78rem; }
  .empty { text-align: center; padding: 2rem; color: var(--text-muted); }

  .pill { display: inline-block; padding: 0.15rem 0.5rem; border-radius: 999px; font-size: 0.75rem; font-weight: 700;
    background: color-mix(in srgb, var(--brand,#0d9488) 8%, transparent); color: var(--brand,#0d9488); }
  .status { font-size: 0.75rem; font-weight: 600; color: var(--text-muted); }
  .status.on { color: #059669; }

  .result-card { margin-top: 0.75rem; background: rgba(245,158,11,0.06); border: 1px solid rgba(245,158,11,0.2);
    border-radius: 0.85rem; overflow: hidden; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
  .result-hd { padding: 0.6rem 0.85rem; background: rgba(245,158,11,0.08); font-weight: 700; font-size: 0.8rem; color: #b45309; }
  .result-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.5rem; padding: 0.85rem; }
  .r-item { display: flex; flex-direction: column; gap: 0.15rem; padding: 0.5rem 0.6rem; background: rgba(255,255,255,0.5); border-radius: 0.5rem; }
  .r-item span { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--text-muted); }
  .r-item strong { font-size: 1rem; color: var(--text-primary); font-weight: 800; }
  .r-item.accent strong { color: #059669; }
  .r-item.warn strong { color: #b45309; }

  @media (max-width: 500px) { .result-grid { grid-template-columns: 1fr 1fr; } }
</style>
